<template>
  <div class="onboarding-popup">
    <v-dropdown ref="dropdown">
      <v-btn class="bg-icon" icon @click="onButtonClick">
        <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="15" cy="15" r="11.25" stroke="white" stroke-width="2" />
          <circle cx="15" cy="22.5" r="0.625" fill="white" stroke="white" />
          <path
            d="M15 20V18.2264C15 17.0461 15.7553 15.9982 16.875 15.625V15.625C17.9947 15.2518 18.75 14.2039 18.75 13.0236V12.3821C18.75 10.3762 17.1238 8.75 15.1179 8.75H15C12.9289 8.75 11.25 10.4289 11.25 12.5V12.5"
            stroke="white" stroke-width="2" />
        </svg>
      </v-btn>
      <v-card elevated slot="content" class="dropdown-content">
        <div class="onboarding-list-wrap">
          <div class="section-title" v-if="currentSectionTitle">
            {{ currentSectionTitle }}
          </div>
          <div class="onboarding-list" 
               :class="listHeightClass" 
               :style="dynamicListStyle"
               ref="onboardingList">
            <div v-for="item in onboardingItems" :key="item.id" class="onboarding-item">
              <div class="item-content">
                <span class="item-content-video" @click="playItem(item)">
                  <span class="item-text">{{ item.text }}</span>
                  <div class="item-actions">
                    <v-btn v-if="item.video_link" class="action-btn info-btn" icon small >
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9C0 4.02944 4.02944 0 9 0ZM7.7832 4.99023C6.98347 4.54594 6.00025 5.12429 6 6.03906V11.9609C6.00025 12.8757 6.98347 13.4541 7.7832 13.0098L13.4268 9.87402C14.1122 9.49297 14.1122 8.50703 13.4268 8.12598L7.7832 4.99023Z"
                          fill="white" />
                      </svg>
                    </v-btn>
                  </div>
                </span>
                <v-btn class="action-btn info-btn" icon small @click="playItemText(item)">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M5 18H10C11.1046 18 12 18.8954 12 20V11C12 8.17157 12 6.75736 11.1213 5.87868C10.2426 5 8.82843 5 6 5H5C4.05719 5 3.58579 5 3.29289 5.29289C3 5.58579 3 6.05719 3 7V16C3 16.9428 3 17.4142 3.29289 17.7071C3.58579 18 4.05719 18 5 18Z"
                      fill="white" />
                    <path
                      d="M19 18H14C12.8954 18 12 18.8954 12 20V11C12 8.17157 12 6.75736 12.8787 5.87868C13.7574 5 15.1716 5 18 5H19C19.9428 5 20.4142 5 20.7071 5.29289C21 5.58579 21 6.05719 21 7V16C21 16.9428 21 17.4142 20.7071 17.7071C20.4142 18 19.9428 18 19 18Z"
                      fill="white" />
                  </svg>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-card>
    </v-dropdown>
  </div>
</template>

<script>
import sectionsData from '@/onboardingData/data.json'
import sectionsDataEN from '@/onboardingData/data-en.json'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'OnboardingButton',

  data: () => ({
    calculatedHeight: null,
    isDropdownOpen: false
  }),

  computed: {
    ...mapGetters('onboarding', [
      'showOnboardingModal',
      'showOnboardingNotionModal'
    ]),

    lang () {
      const language = this.$store.getters['account/lang']
      return language === 'us' ? 'en' : language
    },

    sections () {
      return this.lang === 'uk' ? sectionsData : sectionsDataEN
    },

    isAuth () {
      return this.$store.getters['account/isAuth']
    },

    currentRoute () {
      return this.$route.path
    },

    currentSectionKey () {
      if (this.currentRoute.includes('/planning') || this.currentRoute.includes('/kkz')) {
        return 'planning'
      }
      if (this.currentRoute.includes('/reports')) {
        return 'reports'
      }
      if (this.currentRoute.includes('/animal-groups')) {
        return 'animals'
      }
      if (this.currentRoute.includes('/stock')) {
        return 'warehouse'
      }
      if (this.currentRoute.includes('/rations')) {
        return 'rations'
      }
      if (this.currentRoute.includes('/history')) {
        return 'history'
      }
      if (this.currentRoute.includes('/admin/users')) {
        return 'users'
      }
      if (this.currentRoute.includes('/admin/groups')) {
        return 'access_groups'
      }
      if (this.currentRoute.includes('/admin/animal-types')) {
        return 'animal_types'
      }
      if (this.currentRoute.includes('/admin/ingredient-types')) {
        return 'ingredient_types'
      }
      if (this.currentRoute.includes('/admin/mixers')) {
        return 'mixers'
      }

      return 'general_settings'
    },

    currentSection () {
      return this.sections[this.currentSectionKey] || this.sections.general_settings
    },

    currentSectionTitle () {
      return this.currentSection.title
    },

    onboardingItems () {
      return this.currentSection.onboardingItems || []
    },

    listHeightClass () {
      return 'dynamic-height'
    },

    dynamicListStyle () {
      if (this.calculatedHeight) {
        return {
          height: `${this.calculatedHeight}px`
        }
      }
      return {}
    }
  },

  methods: {
    ...mapActions('onboarding', [
      'openOnboardingModal',
      'openOnboardingNotionModal',
      'closeOnboardingModal'
    ]),

    calculateOptimalHeight () {
      if (!this.isDropdownOpen) {
        return
      }

      this.$nextTick(() => {
        const listElement = this.$refs.onboardingList
        if (!listElement) {
          return
        }

        listElement.style.height = 'auto'
        listElement.style.overflowY = 'hidden'

        const items = listElement.querySelectorAll('.onboarding-item')
        const maxVisibleItems = 8

        if (items.length <= maxVisibleItems) {
          let totalHeight = 0
          for (let i = 0; i < items.length; i++) {
            totalHeight += items[i].offsetHeight
          }

          const containerPadding = 16
          totalHeight += containerPadding

          this.calculatedHeight = Math.max(totalHeight, 56)

          listElement.style.overflowY = 'hidden'
        } else {
          let totalHeight = 0
          for (let i = 0; i < maxVisibleItems; i++) {
            totalHeight += items[i].offsetHeight
          }

          const containerPadding = 14
          const buffer = 14
          totalHeight += containerPadding + buffer

          this.calculatedHeight = totalHeight

          listElement.style.height = `${this.calculatedHeight}px`
          listElement.style.overflowY = 'auto'
          listElement.style.maxHeight = `${this.calculatedHeight}px`
        }
      })
    },

    onButtonClick () {
      setTimeout(() => {
        this.isDropdownOpen = true
        this.calculateOptimalHeight()
      }, 100)
    },

    closeDropdown () {
      if (this.$refs.dropdown) {
        this.$refs.dropdown.closeDropdown()
      }
    },

    playItem (item) {
      this.openOnboardingModal(item)
      this.closeDropdown()
    },

    playItemText (item) {
      const sectionItem = {
        ...item,
        section: this.currentSectionKey
      }

      this.openOnboardingNotionModal(sectionItem)
      this.closeDropdown()
    },
  },

  watch: {
    showOnboardingModal (isOpen) {
      if (isOpen) {
        this.closeDropdown()
      }
    },

    showOnboardingNotionModal (isOpen) {
      if (isOpen) {
        this.closeDropdown()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/sass/abstracts/_variables.scss';

.onboarding-popup {
  .bg-icon {
    border-radius: 1rem;
    background-color: #1DA643;
    width: 4.6rem;
    height: 4.6rem;
  }

  &:hover {
    .bg-icon {
      background-color: #5EBE40;
    }
  }
}

.dropdown-content {
  min-width: 32rem;
  max-width: 40rem;
  padding: 0;
  left: -13.6rem;
  position: relative;
  top: 1.2rem;

  @media screen and (max-width: 800px) {
    left: -24rem;
  }
}

.onboarding-list-wrap {
  padding: 0.8rem;
  padding-right: 0.5rem;
  border-radius: 1rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -0.8rem;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 0.8rem solid transparent;
    border-right: 0.8rem solid transparent;
    border-bottom: 0.8rem solid #fff;

    @media screen and (max-width: 800px) {
      display: none;
    }
  }
}

.section-title {
  font-size: 1.6rem;
  font-weight: 600;
  color: #333;
  padding: 0.8rem 1rem;
  border-bottom: 0.1rem solid #eee;
  margin-bottom: 0.8rem;
}

.onboarding-list::-webkit-scrollbar {
  width: 0.6rem;
}

.onboarding-list::-webkit-scrollbar-track {
  background: transparent;
}

.onboarding-list::-webkit-scrollbar-thumb {
  background-color: #888;
  border-radius: 0.3rem;
  border: 0.1rem solid transparent;
}

.item-content-video {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 1rem;
  min-height: 4.8rem;
  padding-left: 0.8rem;
}

.onboarding-list {
  scrollbar-width: thin;
  scrollbar-color: #59CC56 transparent;
  overflow-x: hidden;
  overflow-y: hidden;
  padding: 0.8rem 1rem;
  padding-right: 0.8rem;
  transition: height 0.3s ease;
  
  &.dynamic-height {
  }
  
  .onboarding-item {
    padding-right: 0.8rem;
    margin-right: 0.8rem;
    cursor: pointer;
    transition: background-color 0.2s ease;
    border-radius: 1rem;

    &:hover {
      background-color: #eee;
    }

    &:last-child {
      border-bottom: none;
    }
  }

  .item-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1.2rem;
  }

  .item-text {
    flex: 1;
    font-size: 1.4rem;
    line-height: 1.4;
    color: #333;
    padding: 0.8rem 0;
    word-wrap: break-word;
    white-space: normal;
  }

  .item-actions {
    display: flex;
    gap: 0.8rem;
    flex-shrink: 0;
    align-self: center;
  }

  .action-btn {
    width: 3.2rem !important;
    height: 3.2rem !important;
    border-radius: 0.6rem;

    &.video-btn {
      background-color: #e53e3e !important;

      &:hover {
        background-color: #c53030 !important;
      }
    }

    &.info-btn {
      background-color: #1DA643 !important;

      &:hover {
        background-color: #5EBE40 !important;
      }
    }
  }
}
</style>